import { types } from "mobx-state-tree";
import { DATE_FORMAT, DATE_TIME_FORMAT } from "modules/common/constants";
import { BaseEntity } from "modules/common/models/entity";
import { asFileBase } from "modules/common/models/file-metadata";
import { Notificator } from "modules/common/models/notificator";
import { formatDate } from "modules/common/services/formatting/date";
import { nameof } from "modules/common/services/typescript";
import moment from "moment";
import { OrderConfirmFile, OrderConfirmFileType } from "./order-file";
import { StringifiedOrderLink } from "./order-link";
import { DictionaryLink } from "modules/common/models/dictionary-link";

export const SimpleQuestion = types
  .model({
    id: types.string,
    title: types.optional(types.string, ""),
    description: types.string,
    stopDate: types.string,
    stopDay: types.string,
    number: types.string,
    completed: types.boolean,
  })
  .named("SimpleQuestion");

export const QuestionReadiness = types
  .model({
    employer: types.maybeNull(DictionaryLink),
    day: types.optional(types.string, ""),
  })
  .named("QuestionReadiness");

export const Message = types
  .model({
    id: types.string,
    description: types.string,
    date: types.string,
    day: types.string,
    hours: types.string,
    employer: types.maybeNull(DictionaryLink),
  })
  .named("Message");

export const OrderQuestion = types
  .compose(
    BaseEntity,
    Notificator,
    types.model({
      stopDate: types.string,
      day: types.string,
      stopDay: types.string,
      stopDateSortable: types.string,
      stopDays: types.array(types.string),
      oldStopDates: types.array(types.string),
      completed: types.boolean,
      dateSortable: types.string,
      orderId: types.string,
      order: types.maybeNull(StringifiedOrderLink),
      description: types.string,
      number: types.string,
      messages: types.array(Message),
      author: types.maybeNull(DictionaryLink),
      employer: types.maybeNull(DictionaryLink),
      implementer: types.maybeNull(DictionaryLink),
      employers: types.array(DictionaryLink),
      implementers: types.array(DictionaryLink),
      title: types.optional(types.string, ""),
      tags: types.array(types.string),
      /** Кто из исполнителей отметил, что закончил работу */
      readiness: types.array(QuestionReadiness),
      reviewSentBy: types.maybeNull(DictionaryLink),
      reviewSentDate: types.maybeNull(types.string),
      columnId: types.optional(types.string, ""),
      sortOrder: types.optional(types.number, 0),
      portfolioId: types.optional(types.string, ""),
      canMoveQuestion: types.optional(types.boolean, false),
      canCompleteQuestion: types.optional(types.boolean, false),
      /** Можно отмечать готовность за исполнителей: ответственный, автор, ГИП, полный доступ */
      canManageReadiness: types.optional(types.boolean, false),
      questionFiles: types.array(OrderConfirmFile),
      answerFiles: types.array(OrderConfirmFile),
      answer: types.string,
      canEditQuestion: types.boolean,
      canRemoveQuestion: types.boolean,
      canAddAnswer: types.boolean,
      canAddImplementer: types.boolean,
      canChangeCompleted: types.boolean,
      orderStatusId: types.string,
      parent: types.maybeNull(types.string),
      parentQuestion: types.maybeNull(SimpleQuestion),
      selfQuestion: types.boolean,
      children: types.array(SimpleQuestion),
    })
  )
  .views((self) => ({
    get dateAsDate() {
      return moment(self.created, DATE_TIME_FORMAT);
    },
    /** Заголовок карточки: у старых заданий его нет, показываем номер */
    get label() {
      return self.title || `Задание ${self.number}`;
    },
    get documentsAsMap() {
      let docs: OrderConfirmFileType[] = [];

      docs = self.questionFiles.concat(self.answerFiles);

      return docs.reduce((result, doc) => {
        result[doc.id] = doc;
        return result;
      }, {} as TStringMap<OrderConfirmFileType>);
    },
  }))
  .named("OrderQuestion");

/** Список сотрудников задания: одиночное поле остаётся ради старых записей и мобильного приложения */
const people = (
  many: { id: string }[] | undefined,
  single: { id: string } | null | undefined
): string[] => {
  if (many && many.length > 0) {
    return many.map((p) => p.id);
  }

  return single ? [single.id] : [];
};

export const mapQuestion = (
  question: OrderQuestionSnapshotType,
  orderGip?: string | null,
  parent?: OrderQuestionSnapshotType | null
): SaveQuestion => {
  const employers = parent
    ? people(parent.employers, parent.employer)
    : !question.id && orderGip
    ? [orderGip]
    : people(question.employers, question.employer);

  return {
    id: question.id,
    orderId: parent ? parent.orderId : question.orderId,
    portfolioId: parent ? parent.portfolioId ?? "" : question.portfolioId ?? "",
    columnId: question.columnId || "",
    title: question.title ?? "",
    tags: question.tags ? [...question.tags] : [],
    tagColors: {},
    description: question.description,
    completed: question.completed,
    // stopDay приходит пустым, когда срока нет; stopDate у таких заданий равен 01.01.0001
    stopDate: question.stopDay || "",
    employerIds: employers,
    implementerIds: people(question.implementers, question.implementer),
    questionFiles: question.questionFiles.map(asFileBase),
    answerFiles: question.answerFiles.map(asFileBase),
    answer: question.answer,
    messages: question.messages,
    parent: parent ? parent.id : question.parent,
    parentQuestion: parent ? parent : null,
    selfQuestion: question.selfQuestion,
  };
};

export interface SaveQuestion {
  id: string;
  stopDate: string;
  orderId: string;
  portfolioId: string;
  columnId: string;
  title: string;
  tags: string[];
  /** Цвета меток, которые завели или перекрасили в этом окне: название → цвет */
  tagColors: TStringMap<string>;
  description: string;
  employerIds: string[];
  implementerIds: string[];
  completed: boolean;
  selfQuestion: boolean;
  parent: string | null;
  parentQuestion: OrderQuestionSnapshotType | null;
  questionFiles: FileBase[];
  answerFiles: FileBase[];
  answer: string;
  messages: MessageType[];
}

export const tableFields = {
  stopDate: nameof((a: OrderQuestionType) => a.stopDate) as string,
  orderId: nameof((a: OrderQuestionType) => a.orderId) as string,
  description: nameof((a: OrderQuestionType) => a.description) as string,
  completed: nameof((a: OrderQuestionType) => a.completed) as string,
  author: nameof((a: OrderQuestionType) => a.author) as string,
  employer: nameof((a: OrderQuestionType) => a.employer) as string,
  implementer: nameof((a: OrderQuestionType) => a.implementer) as string,
  questionFiles: nameof((a: OrderQuestionType) => a.questionFiles) as string,
  answerFiles: nameof((a: OrderQuestionType) => a.answerFiles) as string,
  messages: nameof((a: OrderQuestionType) => a.messages) as string,
  stopDays: nameof((a: OrderQuestionType) => a.stopDays) as string,
  oldStopDates: nameof((a: OrderQuestionType) => a.oldStopDates) as string,
  answer: nameof((a: OrderQuestionType) => a.answer) as string,
  order: nameof((a: OrderQuestionType) => a.order) as string,
  day: nameof((a: OrderQuestionType) => a.day) as string,
  stopDay: nameof((a: OrderQuestionType) => a.stopDay) as string,
  number: nameof((a: OrderQuestionType) => a.number) as string,
};

export const questionFields = {
  id: nameof((a: SaveQuestion) => a.id) as string,
  stopDate: nameof((a: SaveQuestion) => a.stopDate) as string,
  orderId: nameof((a: SaveQuestion) => a.orderId) as string,
  portfolioId: nameof((a: SaveQuestion) => a.portfolioId) as string,
  columnId: nameof((a: SaveQuestion) => a.columnId) as string,
  title: nameof((a: SaveQuestion) => a.title) as string,
  tags: nameof((a: SaveQuestion) => a.tags) as string,
  tagColors: nameof((a: SaveQuestion) => a.tagColors) as string,
  description: nameof((a: SaveQuestion) => a.description) as string,
  completed: nameof((a: SaveQuestion) => a.completed) as string,
  employerIds: nameof((a: SaveQuestion) => a.employerIds) as string,
  implementerIds: nameof((a: SaveQuestion) => a.implementerIds) as string,
  questionFiles: nameof((a: SaveQuestion) => a.questionFiles) as string,
  answerFiles: nameof((a: SaveQuestion) => a.answerFiles) as string,
  answer: nameof((a: SaveQuestion) => a.answer) as string,
  messages: nameof((a: SaveQuestion) => a.messages) as string,
  selfQuestion: nameof((a: SaveQuestion) => a.selfQuestion) as string,
  parent: nameof((a: SaveQuestion) => a.parent) as string,
  parentQuestion: nameof((a: SaveQuestion) => a.parentQuestion) as string,
};

export type OrderQuestionType = typeof OrderQuestion.Type;
export type OrderQuestionSnapshotType = typeof OrderQuestion.SnapshotType;

export type MessageType = typeof Message.Type;
export type MessageSnapshotType = typeof Message.SnapshotType;

export type SimpleQuestionType = typeof SimpleQuestion.Type;
export type SimpleQuestionSnapshotType = typeof SimpleQuestion.SnapshotType;

export const initialState = (): OrderQuestionSnapshotType => ({
  id: "",
  selfQuestion: false,
  parent: null,
  parentQuestion: null,
  children: [],
  created: formatDate(new Date(), DATE_FORMAT),
  orderId: "",
  portfolioId: "",
  columnId: "",
  sortOrder: 0,
  title: "",
  tags: [],
  readiness: [],
  reviewSentBy: null,
  reviewSentDate: null,
  stopDate: "",
  author: null,
  employer: null,
  implementer: null,
  employers: [],
  implementers: [],
  order: null,
  day: "",
  stopDay: "",
  stopDateSortable: "",
  dateSortable: "",
  description: "",
  completed: false,
  questionFiles: [],
  stopDays: [],
  oldStopDates: [],
  answerFiles: [],
  answer: "",
  messages: [],
  canEditQuestion: false,
  canRemoveQuestion: false,
  canAddAnswer: false,
  canAddImplementer: false,
  canChangeCompleted: false,
  canMoveQuestion: false,
  canCompleteQuestion: false,
  canManageReadiness: false,
  number: "",
  orderStatusId: "",
});
